import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "./components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./components/ui/Card";
import { Progress } from "./components/ui/Progress";
import { healthAssessmentQuestions, QUESTIONS_PER_QUIZ } from "./lib/questions";
import type {
  Question,
  QuestionResponse,
  HealthAssessmentResult,
  APIRecommendationResponse,
} from "./types/quiz";
import QuestionCard from "./components/quiz/QuestionCard";
import "./design/global.css";
import "./App.css";
import HeaderAd from "./components/ads/HeaderAd";
import InContentAd from "./components/ads/InContentAd";

function App() {
  const hasTrackedViewContent = useRef(false);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<QuestionResponse[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [assessmentComplete, setAssessmentComplete] = useState(false);
  const [recommendations, setRecommendations] =
    useState<APIRecommendationResponse | null>(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationError, setRecommendationError] = useState<string | null>(
    null,
  );

  const trackMetaPixel = (
    eventType: "track" | "trackCustom",
    eventName: string,
    params?: Record<string, unknown>,
  ) => {
    if (typeof window === "undefined") return;

    const fbq = (
      window as Window & {
        fbq?: (...args: unknown[]) => void;
      }
    ).fbq;

    if (typeof fbq === "function") {
      if (params) {
        fbq(eventType, eventName, params);
      } else {
        fbq(eventType, eventName);
      }
    }
  };

  useEffect(() => {
    if (assessmentStarted && !hasTrackedViewContent.current) {
      trackMetaPixel("track", "ViewContent");
      hasTrackedViewContent.current = true;
    }
  }, [assessmentStarted]);

  // ------------------ Load Questions ------------------
  const loadQuestions = useCallback(() => {
    setQuestions(healthAssessmentQuestions.slice(0, QUESTIONS_PER_QUIZ));
    setCurrentQuestionIndex(0);
    setResponses([]);
    setSelectedAnswers([]);
    setAssessmentComplete(false);
    setRecommendations(null);
    setLoadingRecommendations(false);
    setRecommendationError(null);
  }, []);

  const handleStartHealthCheck = () => {
    setAssessmentStarted(true);
    trackMetaPixel("trackCustom", "QuizStart");
    loadQuestions();
  };

  // ------------------ Answer Handling ------------------
  const handleAnswerSelect = (optionIndex: number) => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion.type === "single") {
      setSelectedAnswers([optionIndex]);
    } else {
      setSelectedAnswers((prev) =>
        prev.includes(optionIndex)
          ? prev.filter((i) => i !== optionIndex)
          : [...prev, optionIndex],
      );
    }
  };

  const handleConfirmAnswer = () => {
    if (questions.length === 0 || selectedAnswers.length === 0) return;

    const currentQuestion = questions[currentQuestionIndex];
    const response: QuestionResponse = {
      questionId: currentQuestion.id,
      selectedAnswers: [...selectedAnswers],
      timestamp: new Date(),
    };

    setResponses((prev) => [...prev, response]);

    // Automatically move to next question or complete assessment
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswers([]);
    } else {
      setAssessmentComplete(true);
      trackMetaPixel("trackCustom", "QuizComplete", {
        content_name: "Health Assessment Quiz",
      });
      const finalResponses = [...responses, response];
      const result: HealthAssessmentResult = {
        responses: finalResponses,
        totalQuestionsAnswered: finalResponses.length,
        completedAt: new Date(),
        assessmentType: "health_check",
      };
      localStorage.setItem(
        "healthAssessmentResult",
        JSON.stringify(result, null, 2),
      );
      fetchHealthRecommendations(result);
    }
  };

  // ------------------ Fetch Recommendations ------------------
  const fetchHealthRecommendations = async (
    assessmentData: HealthAssessmentResult,
  ) => {
    setLoadingRecommendations(true);
    setRecommendationError(null);

    const BACKEND_URL =
      "https://google-fit-backend-git-main-hadaya-qaisers-projects.vercel.app";

    try {
      // Transform responses to match backend expectations
      const transformedResponses = assessmentData.responses.map((response) => {
        const question = healthAssessmentQuestions.find(
          (q) => q.id === response.questionId,
        );
        const selectedAnswerTexts = response.selectedAnswers
          .map((index) => (question ? question.options[index] : ""))
          .filter((text) => text !== "");

        return {
          questionId: response.questionId,
          selectedAnswers: selectedAnswerTexts, // Convert from indices to text
          timestamp: response.timestamp,
        };
      });

      const response = await fetch(`${BACKEND_URL}/api/health/recommendation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          responses: transformedResponses,
          questions: healthAssessmentQuestions,
          completedAt: assessmentData.completedAt,
          assessmentType: assessmentData.assessmentType,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      const recommendationData: APIRecommendationResponse =
        await response.json();
      setRecommendations(recommendationData);
    } catch (error) {
      setRecommendationError(
        "Unable to connect to health analysis service. Please check your connection and try again.",
      );
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const restartAssessment = () => loadQuestions();

  // ------------------ Render ------------------
  if (!assessmentStarted) {
    return (
      <div className="App">
        <div className="container">
          <h1 className="title">Health Assessment 🏥</h1>
          <p className="subtitle">
            A personalized health and wellness questionnaire
          </p>
          <button className="start-button" onClick={handleStartHealthCheck}>
            Start your health assessment
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0 && !assessmentComplete) {
    return <p>Loading Health Assessment...</p>;
  }

  return (
    <div className="quiz-container">
      <div className="quiz-content">
        {assessmentComplete ? (
          loadingRecommendations ? (
            <div className="loading-container">
              <h2>🔍 Analyzing Your Results...</h2>
              <p>
                Please wait while we generate your personalized health
                recommendations.
              </p>
            </div>
          ) : recommendationError ? (
            <div className="error-container">
              <h2>⚠️ Analysis Unavailable</h2>
              <p>{recommendationError}</p>
              <button onClick={restartAssessment} className="restart-button">
                Take Assessment Again
              </button>
            </div>
          ) : recommendations ? (
            <div className="health-results-container">
              {/* Header Section */}
              <div className="results-header">
                <div className="results-title">
                  <h2>🏥 Your Health Assessment Results</h2>
                  <p className="results-subtitle">
                    Comprehensive analysis of your health and wellness profile
                  </p>
                </div>
              </div>

              {/* Score Section */}
              <div className="health-score-card">
                <div className="score-display">
                  <div className="score-circle">
                    <div className="score-number">
                      {recommendations.healthScore}
                    </div>
                    <div className="score-label">Health Score</div>
                  </div>
                  <div className="risk-assessment">
                    <span
                      className={`risk-badge risk-${recommendations.riskLevel.toLowerCase()}`}
                    >
                      {recommendations.riskLevel.charAt(0).toUpperCase() +
                        recommendations.riskLevel.slice(1)}{" "}
                      Risk
                    </span>
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div className="assessment-summary-card">
                <div className="card-header">
                  <h3>📋 Assessment Overview</h3>
                </div>
                <div className="card-content">
                  <p className="summary-text">{recommendations.summary}</p>
                </div>
              </div>

              <div className="ebooks-callout">
                <div className="ebooks-callout__content">
                  <span className="ebooks-callout__eyebrow">
                    Free resources
                  </span>
                  <h3>Download our Free E-books</h3>
                  <p>
                    Explore helpful wellness guides and practical health tips on
                    our downloads page.
                  </p>
                </div>
                <a
                  className="ebooks-callout__link"
                  href="https://hoorvices.com/downloads"
                  onClick={() =>
                    trackMetaPixel("trackCustom", "DownloadsVisit", {
                      content_name: "Free E-books",
                    })
                  }
                >
                  View Downloads
                </a>
              </div>

              {/* Grid Layout for Details */}
              <div className="results-grid">
                {/* Priority Areas */}
                <div className="result-card priority-card">
                  <div className="card-header">
                    <h3>🎯 Priority Areas</h3>
                    <span className="card-badge">
                      {recommendations.priorityAreas.length} Areas
                    </span>
                  </div>
                  <div className="card-content">
                    <ul className="result-list">
                      {recommendations.priorityAreas.map((area, index) => (
                        <li key={index} className="list-item priority-item">
                          <div className="item-icon">⚠️</div>
                          <span>{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Strengths */}
                <div className="result-card strengths-card">
                  <div className="card-header">
                    <h3>💪 Your Strengths</h3>
                    <span className="card-badge">
                      {recommendations.strengths.length} Strengths
                    </span>
                  </div>
                  <div className="card-content">
                    <ul className="result-list">
                      {recommendations.strengths.map((strength, index) => (
                        <li key={index} className="list-item strength-item">
                          <div className="item-icon">✅</div>
                          <span>{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Immediate Actions */}
                <div className="result-card actions-card">
                  <div className="card-header">
                    <h3>⚡ Action Steps</h3>
                    <span className="card-badge">
                      {recommendations.immediateActions.length} Actions
                    </span>
                  </div>
                  <div className="card-content">
                    <ul className="result-list">
                      {recommendations.immediateActions.map((action, index) => (
                        <li key={index} className="list-item action-item">
                          <div className="item-icon">🔥</div>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="results-footer">
                <div className="assessment-meta">
                  <div className="meta-item">
                    <span className="meta-icon">📊</span>
                    <span>
                      Completed:{" "}
                      {new Date(
                        recommendations.assessmentDate,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-icon">📝</span>
                    <span>{recommendations.questionsAnalyzed}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-icon">🔬</span>
                    <span>Source: Expert Health Analyzer</span>
                    {/* {recommendations.source} */}
                    {/* before it was above, this is hard coded data - change in backend - the source later */}
                  </div>
                </div>

                <div className="results-actions">
                  <Button
                    onClick={restartAssessment}
                    className="restart-button"
                  >
                    <span>🔄</span>
                    Take Assessment Again
                  </Button>
                </div>
              </div>
            </div>
          ) : null
        ) : (
          <>
            <HeaderAd />
            <Card className="quiz-card">
              <CardHeader>
                <div>
                  Progress: {currentQuestionIndex + 1} of {questions.length}
                  <Progress
                    value={
                      ((currentQuestionIndex + 1) / questions.length) * 100
                    }
                  />
                </div>
                <CardTitle>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </CardTitle>
                <CardDescription>
                  {questions[currentQuestionIndex].type === "multiple"
                    ? "Select all that apply"
                    : "Select one option"}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <QuestionCard
                  question={questions[currentQuestionIndex]}
                  selectedAnswers={selectedAnswers}
                  onAnswerSelect={handleAnswerSelect}
                />
              </CardContent>

              <CardFooter>
                <Button
                  onClick={handleConfirmAnswer}
                  disabled={selectedAnswers.length === 0}
                >
                  {currentQuestionIndex === questions.length - 1
                    ? "Complete Assessment 🏆"
                    : "Confirm Answer ➡️"}
                </Button>
              </CardFooter>
            </Card>
          </>
        )}
      </div>
      <InContentAd />
    </div>
  );
}

export default App;
